import { Button, Card, CardBody, Col, Form, Input, Label, Row, FormFeedback } from 'reactstrap';
import CommonCardHeader from '../../../../coreComponents/CommonCardHeader';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../reduxToolkit/hooks';
import { sendFeedback } from '../../../../services/api';
import Swal from 'sweetalert2';
import { Primary } from '../../../../constants';

type FeedBackFormProps = { close: () => void };

const MAX_SUBJECT = 255;
const MAX_MESSAGE = 5000;

const FeedBackForm = ({ close }: FeedBackFormProps) => {
  const { t } = useTranslation();
  const { email, id } = useAppSelector((state) => state.auth);
  const [isSending, setIsSending] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState({ subject: false, message: false });
  const [submitted, setSubmitted] = useState(false);

  const subjectEmpty = subject.trim().length === 0;
  const subjectTooLong = subject.length > MAX_SUBJECT;
  const messageEmpty = message.trim().length === 0;
  const messageTooLong = message.length > MAX_MESSAGE;

  const showSubjectError = (submitted || touched.subject) && (subjectEmpty || subjectTooLong);
  const showMessageError = (submitted || touched.message) && (messageEmpty || messageTooLong);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    setSubmitted(true); // start showing errors after the first submit attempt

    if (subjectEmpty || subjectTooLong || messageEmpty || messageTooLong) return;

    // Extra safety (in case maxLength is bypassed by paste/automation)
    if (subjectTooLong || messageTooLong || subjectEmpty || messageEmpty) {
      Swal.fire({
        title: t('feedback.form.validationError'),
        text:
          (subjectEmpty ? t('feedback.form.subjectRequired') : '') +
          (messageEmpty ? t('feedback.form.messageRequired') : '') +
          (subjectTooLong ? t('feedback.form.subjectLimit', { count: MAX_SUBJECT }) : '') +
          (messageTooLong ? t('feedback.form.messageLimit', { count: MAX_MESSAGE }) : ''),
        icon: 'error',
        confirmButtonColor: Primary,
      });
      return;
    }

    const payload = {
      userId: id,
      userEmail: email,
      subject: subject.trim(),
      message: message.trim(),
    };

    try {
      const res = await sendFeedback(payload);
      if (res?.success) {
        Swal.fire({
          title: t('feedback.notifications.successTitle'),
          text: t('feedback.notifications.successSent'),
          icon: 'success',
          confirmButtonColor: Primary,
        }).then(() => close());
      } else {
        Swal.fire({
          title: t('feedback.notifications.errorTitle'),
          text: t('feedback.notifications.errorSent'),
          icon: 'error',
          confirmButtonColor: Primary,
        });
      }
    } catch (error) {
      Swal.fire({
        title: t('feedback.notifications.errorTitle'),
        text: t('feedback.notifications.errorSent'),
        icon: 'error',
        confirmButtonColor: Primary,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CommonCardHeader title={t('feedback.sendFeedback')} tag />
      <CardBody>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md="12">
              <Label htmlFor="inputSubject">{t('feedback.subject')}</Label>
              <Input
                id="inputSubject"
                type="text"
                placeholder={t('feedback.subjectPlaceholder')}
                value={subject}
                maxLength={MAX_SUBJECT}
                invalid={showSubjectError}
                onBlur={() => setTouched((s) => ({ ...s, subject: true }))}
                onChange={(e) => setSubject(e.target.value)}
              />
              <FormFeedback>
                {subjectEmpty
                  ? t('feedback.form.subjectRequired')
                  : t('feedback.form.subjectLimit', { count: MAX_SUBJECT })}
              </FormFeedback>
              <p className={`text-muted ${subjectTooLong ? 'text-danger' : ''}`}>
                <small>
                  {t('feedback.form.chars')}: {subject.length} / {MAX_SUBJECT}
                </small>
              </p>
            </Col>

            <Col md="12">
              <Label htmlFor="inputMessage">{t("feedback.message")}</Label>
              <Input
                id="inputMessage"
                type="textarea"
                placeholder={ t("feedback.messagePlaceholder")}
                value={message}
                maxLength={MAX_MESSAGE}
                invalid={showMessageError}
                onBlur={() => setTouched((s) => ({ ...s, message: true }))}
                onChange={(e) => setMessage(e.target.value)}
              />
              <FormFeedback>
                {messageEmpty
                  ? t('feedback.form.messageRequired')
                  : t('feedback.form.messageLimit', { count: MAX_MESSAGE })}
              </FormFeedback>
              <p className={`text-muted ${messageTooLong ? 'text-danger' : ''}`}>
                <small>
                  {t('feedback.form.chars')}: {message.length} / {MAX_MESSAGE}
                </small>
              </p>
            </Col>

            <Col xs="12">
              <Row className="g-1">
                <Col xs="2">
                  <Button color="primary" type="submit" disabled={isSending}>
                    {'Ok'}
                  </Button>
                </Col>
                <Col xs="2">
                  <Button color="secondary" type="button" onClick={close} disabled={isSending}>
                    {t('global.cancel')}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
};

export default FeedBackForm;
